import { useMemo, type CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { calculateOffset } from '@509-bender/bend-math';
import { selectionToShoe } from '../lib/bender-selection.js';
import {
  formatDefaultConduitLabel,
  settingsToBenderSelection,
  useAppSettings,
  type AppSettings,
} from '../lib/app-settings.js';
import { getStoredCalculatorValues } from '../lib/calculator-values.js';
import { formatInches } from '../lib/format.js';
import { useLastCalculatorId } from '../lib/recent-calculator.js';
import { useSubscription } from '../lib/subscription.js';
import { CALCULATORS, type CalculatorConfig } from './calculator-configs.js';

const PRIMARY_IDS = ['offset', 'ninety', 'saddle3'];
const CALCULATOR_IDS = CALCULATORS.map((cfg) => cfg.id);

const QUICK_LABELS: Record<string, string> = {
  ninety: 'Single bend',
  offset: 'Around obstacle',
  saddle3: 'Over pipe',
  'back-to-back': 'Two matching stub-ups',
  saddle4: 'Wide obstruction',
  kick: 'Box offset',
  'rolling-offset': 'Rise and roll',
  'parallel-offset': 'Matching offsets',
  concentric: 'Parallel offsets',
  segmented: 'Large radius sweep',
};

const RESULT_CUES: Record<string, string> = {
  ninety: 'Mark from end',
  saddle3: '3 marks + shrink',
  'back-to-back': '2 marks + cut',
  saddle4: '4 marks + shrink',
  kick: '90 mark + kick',
  'rolling-offset': 'True offset',
  'parallel-offset': 'Mark progression',
  concentric: 'Outermost add',
  segmented: 'Bend count',
};

export default function HomeScreen() {
  const { isPro } = useSubscription();
  const settings = useAppSettings();
  const lastCalculatorId = useLastCalculatorId(CALCULATOR_IDS);
  const offsetSummary = useMemo(() => getOffsetSummary(settings), [settings]);
  const { primary, secondary } = useMemo(() => {
    const primarySet = new Set(PRIMARY_IDS);
    return {
      primary: PRIMARY_IDS.map((id) => CALCULATORS.find((cfg) => cfg.id === id)).filter(
        (cfg): cfg is CalculatorConfig => Boolean(cfg),
      ),
      secondary: CALCULATORS.filter((cfg) => cfg.requiresPro && !primarySet.has(cfg.id)),
    };
  }, []);
  const lastCalculator = useMemo(
    () => CALCULATORS.find((cfg) => cfg.id === lastCalculatorId),
    [lastCalculatorId],
  );
  const continueCalculator =
    lastCalculator?.id === 'offset' || (lastCalculator?.requiresPro && !isPro)
      ? undefined
      : lastCalculator;
  const offsetEyebrow = lastCalculator?.id === 'offset' ? 'Continue last bend' : 'Fast calculator';

  return (
    <section aria-label="Truebend calculators" style={homeShellStyle}>
      <section aria-label="Fast bend calculators">
        <div style={homeTitleRowStyle}>
          <h1 style={homeTitleStyle}>Pick a bend</h1>
          <span className="mono" style={homeConduitChipStyle}>
            {formatDefaultConduitLabel(settings.defaultConduit, settings.defaultMaterial)}
          </span>
        </div>
        {continueCalculator && <ContinueCalculatorLink config={continueCalculator} />}
        <LevelToolCard locked={!isPro} />
        <SquareAlignToolCard locked={!isPro} />
        {primary.map((cfg, index) =>
          cfg.id === 'offset' ? (
            <FeaturedOffsetCard
              key={cfg.id}
              config={cfg}
              summary={offsetSummary}
              eyebrow={offsetEyebrow}
            />
          ) : (
            <BendCard key={cfg.id} config={cfg} compact={index > 0} />
          ),
        )}
      </section>

      <section style={moreBendsSectionStyle} aria-label="Advanced bend calculators">
        <div style={moreBendsHeaderStyle}>
          <div style={{ display: 'grid', gap: 3, minWidth: 0 }}>
            <div style={moreBendsEyebrowStyle}>Pro tools</div>
            <h2 id="more-bends-heading" style={moreBendsTitleStyle}>
              More bends
            </h2>
            <p style={moreBendsSubStyle}>
              {isPro
                ? 'Advanced layouts for tougher conduit runs.'
                : 'Preview the advanced bend types included with Truebend Pro.'}
            </p>
          </div>
          {!isPro && <span style={moreBendsBadgeStyle}>Pro</span>}
        </div>
        <div style={moreBendsGridStyle}>
          {secondary.map((cfg) => (
            <BendCard key={cfg.id} config={cfg} locked={!isPro} tile />
          ))}
        </div>
      </section>
    </section>
  );
}

function LevelToolCard({ locked }: { locked: boolean }) {
  return (
    <Link
      to="/level"
      aria-label={`Open degree level${locked ? ' (Pro)' : ''}`}
      style={levelToolCardStyle}
    >
      <span aria-hidden="true" style={levelToolIconStyle}>
        <svg viewBox="0 0 56 56" style={{ width: '72%', height: '72%', display: 'block' }}>
          <path
            d="M10 34 H46"
            fill="none"
            stroke="var(--color-blue)"
            strokeLinecap="round"
            strokeWidth="4"
          />
          <path
            d="M14 30 A18 18 0 0 1 42 30"
            fill="none"
            stroke="var(--color-accent)"
            strokeLinecap="round"
            strokeWidth="4"
          />
          <circle cx="28" cy="34" r="5" fill="var(--color-accent)" />
        </svg>
      </span>
      <span style={{ display: 'grid', gap: '2px', minWidth: 0 }}>
        <span style={levelToolTitleStyle}>Degree level</span>
        <span style={levelToolSubStyle}>Zeroable angle finder</span>
      </span>
      <span aria-hidden="true" style={levelToolBadgeStyle}>
        {locked ? 'Pro' : 'Open'}
      </span>
    </Link>
  );
}

function SquareAlignToolCard({ locked }: { locked: boolean }) {
  return (
    <Link
      to="/square-align?target=90"
      aria-label={`Open Square Align${locked ? ' (Pro)' : ''}`}
      style={levelToolCardStyle}
    >
      <span aria-hidden="true" style={levelToolIconStyle}>
        <svg viewBox="0 0 56 56" style={{ width: '72%', height: '72%', display: 'block' }}>
          <path
            d="M11 40 H45 M16 16 V40 M16 16 H42"
            fill="none"
            stroke="var(--color-blue)"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="4"
          />
          <path
            d="M20 36 L40 16"
            fill="none"
            stroke="var(--color-accent)"
            strokeLinecap="round"
            strokeWidth="4"
          />
          <circle cx="40" cy="16" r="5" fill="var(--color-accent)" />
        </svg>
      </span>
      <span style={{ display: 'grid', gap: '2px', minWidth: 0 }}>
        <span style={levelToolTitleStyle}>Square Align</span>
        <span style={levelToolSubStyle}>Building reference</span>
      </span>
      <span aria-hidden="true" style={levelToolBadgeStyle}>
        {locked ? 'Pro' : 'Open'}
      </span>
    </Link>
  );
}

interface OffsetSummary {
  rise: number;
  angle: number;
  distanceBetweenMarks: number;
}

function FeaturedOffsetCard({
  config,
  summary,
  eyebrow,
}: {
  config: CalculatorConfig;
  summary: OffsetSummary;
  eyebrow: string;
}) {
  return (
    <Link to={`/${config.id}`} style={featuredCardStyle} aria-label="Open 2-point Offset">
      <div style={featuredCardTopStyle}>
        <div style={{ minWidth: 0 }}>
          <div style={featuredLabelStyle}>{eyebrow}</div>
          <div style={featuredTitleStyle}>{config.displayName}</div>
          <div style={featuredSubStyle}>Rise, from-end mark, bend angle</div>
        </div>
        <span aria-hidden="true" style={featuredArrowStyle}>
          ›
        </span>
      </div>
      <OffsetPreview angle={summary.angle} distanceBetweenMarks={summary.distanceBetweenMarks} />
      <div style={featuredMetricGridStyle}>
        <MiniMetric label="Rise" value={formatInches(summary.rise)} />
        <MiniMetric label="Bend" value={formatAngle(summary.angle)} />
        <MiniMetric
          label="Between"
          value={formatInches(summary.distanceBetweenMarks)}
          highlighted
        />
      </div>
    </Link>
  );
}

function ContinueCalculatorLink({ config }: { config: CalculatorConfig }) {
  return (
    <Link
      to={`/${config.id}`}
      aria-label={`Continue ${config.displayName}`}
      style={continueLinkStyle}
    >
      <span style={{ display: 'grid', gap: 1, minWidth: 0 }}>
        <span style={continueLabelStyle}>Continue</span>
        <span style={continueTitleStyle}>{config.displayName}</span>
        <span style={continueMetaStyle}>{QUICK_LABELS[config.id] ?? config.shortName}</span>
      </span>
      <span aria-hidden="true" style={continueArrowStyle}>
        {'\u203a'}
      </span>
    </Link>
  );
}

function BendCard({
  config,
  compact = false,
  locked = false,
  tile = false,
}: {
  config: CalculatorConfig;
  compact?: boolean;
  locked?: boolean;
  tile?: boolean;
}) {
  return (
    <Link
      to={`/${config.id}`}
      aria-label={`Open ${config.displayName}${locked ? ' (Pro)' : ''}`}
      style={tile ? tileBendCardStyle : compact ? compactBendCardStyle : bendCardStyle}
    >
      <MiniBendIcon id={config.id} />
      <span style={{ display: 'grid', gap: '2px', minWidth: 0 }}>
        <span style={bendTitleRowStyle(locked)}>
          <span style={bendTitleTextStyle(locked, tile)}>{config.displayName}</span>
          {locked && <span style={lockedBadgeStyle}>Pro</span>}
        </span>
        <span
          style={{
            color: 'var(--color-text-muted)',
            fontSize: 'var(--text-sm)',
            lineHeight: 1.2,
          }}
        >
          {QUICK_LABELS[config.id] ?? config.shortName}
        </span>
        {RESULT_CUES[config.id] && (
          <span className="mono" style={bendResultCueStyle}>
            {RESULT_CUES[config.id]}
          </span>
        )}
      </span>
      <span aria-hidden="true" style={bendChevronStyle}>
        {'\u203a'}
      </span>
    </Link>
  );
}

function OffsetPreview({
  angle,
  distanceBetweenMarks,
}: {
  angle: number;
  distanceBetweenMarks: number;
}) {
  return (
    <div style={offsetPreviewStyle} aria-hidden="true">
      <svg viewBox="0 0 320 122" style={{ width: '100%', height: 'auto', display: 'block' }}>
        <g opacity="0.26" stroke="var(--color-diagram-ghost)" strokeWidth="1">
          {Array.from({ length: 5 }).map((_, i) => (
            <line key={`h-${i}`} x1="14" y1={22 + i * 20} x2="306" y2={22 + i * 20} />
          ))}
          {Array.from({ length: 8 }).map((_, i) => (
            <line key={`v-${i}`} x1={30 + i * 38} y1="12" x2={30 + i * 38} y2="110" />
          ))}
        </g>
        <path
          d="M 24 92 L 94 92 L 158 34 L 296 34"
          fill="none"
          stroke="var(--color-diagram-pipe-shadow)"
          strokeWidth="16"
          strokeLinecap="round"
          strokeLinejoin="round"
          opacity="0.24"
          transform="translate(0 6)"
        />
        <path
          d="M 24 92 L 94 92 L 158 34 L 296 34"
          fill="none"
          stroke="var(--color-diagram-pipe)"
          strokeWidth="10"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M 74 92 L 94 92 L 112 76"
          fill="none"
          stroke="var(--color-blue)"
          strokeWidth="10"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M 142 49 L 158 34 L 184 34"
          fill="none"
          stroke="var(--color-orange)"
          strokeWidth="10"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M 80 80 C 116 38 144 84 180 46"
          fill="none"
          stroke="var(--color-blue)"
          strokeWidth="1.6"
          strokeDasharray="6 7"
          opacity="0.55"
        />
        <text x="184" y="27" fill="var(--color-text-primary)" fontSize="12" fontWeight="850">
          {formatAngle(angle)}
        </text>
        <text
          x="154"
          y="112"
          fill="var(--color-text-primary)"
          fontSize="12"
          fontWeight="850"
          textAnchor="middle"
          fontFamily="var(--font-mono)"
        >
          {formatInches(distanceBetweenMarks)}
        </text>
      </svg>
    </div>
  );
}

function getOffsetSummary(settings: AppSettings): OffsetSummary {
  const config = CALCULATORS.find((cfg) => cfg.id === 'offset');
  const defaults = config ? defaultValuesFor(config) : { rise: 12.5, fromEnd: 8, angle: 30 };
  const values = getStoredCalculatorValues('offset', defaults);
  const rise = positiveValue(values.rise, defaults.rise ?? 12.5);
  const angle = validOffsetAngle(values.angle, defaults.angle ?? 30);
  const shoe = selectionToShoe(settingsToBenderSelection(settings));
  let distanceBetweenMarks = fallbackOffsetDistance(rise, angle);

  if (shoe) {
    try {
      distanceBetweenMarks = calculateOffset({ rise, angle, shoe }).distanceBetweenMarks;
    } catch {
      distanceBetweenMarks = fallbackOffsetDistance(rise, angle);
    }
  }

  return { rise, angle, distanceBetweenMarks };
}

function defaultValuesFor(config: CalculatorConfig): Record<string, number> {
  const init: Record<string, number> = {};
  for (const input of config.inputs) init[input.key] = input.default;
  return init;
}

function positiveValue(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : fallback;
}

function validOffsetAngle(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 && value < 180
    ? value
    : fallback;
}

function fallbackOffsetDistance(rise: number, angle: number): number {
  const sine = Math.sin((angle * Math.PI) / 180);
  return sine > 0 ? rise / sine : rise;
}

function formatAngle(value: number): string {
  if (!Number.isFinite(value)) return '\u2014';
  const rounded = Math.round(value * 10) / 10;
  const text = Number.isInteger(rounded) ? rounded.toFixed(0) : rounded.toFixed(1);
  return `${text}\u00b0`;
}

function MiniMetric({
  label,
  value,
  highlighted = false,
}: {
  label: string;
  value: string;
  highlighted?: boolean;
}) {
  return (
    <span style={highlighted ? miniMetricHighlightedStyle : miniMetricStyle}>
      <span style={highlighted ? miniMetricLabelHighlightedStyle : miniMetricLabelStyle}>
        {label}
      </span>
      <span
        className="mono"
        style={highlighted ? miniMetricValueHighlightedStyle : miniMetricValueStyle}
      >
        {value}
      </span>
    </span>
  );
}

function MiniBendIcon({ id }: { id: string }) {
  const path = iconPathFor(id);
  return (
    <span
      aria-hidden="true"
      style={{
        width: '100%',
        aspectRatio: '1',
        borderRadius: 'var(--radius-md)',
        background: id === 'offset' ? 'var(--color-blue-soft)' : 'rgba(249, 115, 22, 0.12)',
        display: 'grid',
        placeItems: 'center',
      }}
    >
      <svg viewBox="0 0 56 56" style={{ width: '72%', height: '72%', display: 'block' }}>
        <path
          d={path}
          fill="none"
          stroke={id === 'offset' ? 'var(--color-blue)' : 'var(--color-accent)'}
          strokeWidth="4"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  );
}

function iconPathFor(id: string): string {
  switch (id) {
    case 'ninety':
      return 'M16 40 L16 22 Q16 16 22 16 L40 16';
    case 'back-to-back':
      return 'M12 38 L12 24 Q12 18 18 18 L28 18 Q34 18 34 24 L34 30 L44 30';
    case 'offset':
      return 'M10 38 L24 38 L32 24 L46 24';
    case 'saddle3':
      return 'M8 38 L20 38 L28 24 L36 38 L48 38';
    case 'saddle4':
      return 'M8 38 L18 38 L24 28 L32 28 L38 38 L48 38';
    case 'kick':
      return 'M12 40 L30 20 L44 20';
    case 'rolling-offset':
      return 'M8 40 L22 40 L34 26 M24 34 L48 18';
    case 'parallel-offset':
      return 'M8 40 L18 40 L26 28 L42 28 M12 46 L22 46 L30 34 L46 34';
    case 'concentric':
      return 'M10 40 Q12 18 34 18 M16 42 Q18 24 38 24 M22 44 Q24 30 42 30';
    case 'segmented':
      return 'M12 40 Q20 20 44 18 M20 36 L20 40 M28 28 L28 34 M36 22 L36 28';
    default:
      return 'M10 38 L24 38 L32 24 L46 24';
  }
}

const homeShellStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 360px), 1fr))',
  gap: 'var(--space-5)',
  alignItems: 'start',
  width: '100%',
  maxWidth: 920,
  margin: '0 auto',
};

const homeTitleRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 'var(--space-3)',
  marginBottom: 'var(--space-3)',
};

const homeTitleStyle: CSSProperties = {
  fontSize: 'var(--text-3xl)',
  lineHeight: 1,
};

const homeConduitChipStyle: CSSProperties = {
  color: 'var(--color-text-secondary)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-pill)',
  padding: 'var(--space-2) var(--space-3)',
  background: 'var(--color-surface)',
  fontSize: 'var(--text-xs)',
  fontWeight: 850,
  whiteSpace: 'nowrap',
};

const featuredCardStyle: CSSProperties = {
  display: 'grid',
  gap: 'var(--space-3)',
  padding: 'var(--space-4)',
  marginBottom: 'var(--space-3)',
  color: 'var(--color-text-primary)',
  background: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-lg)',
  boxShadow: 'var(--shadow-card)',
  textDecoration: 'none',
};

const featuredCardTopStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: 'var(--space-3)',
};

const featuredLabelStyle: CSSProperties = {
  color: 'var(--color-blue)',
  fontSize: '0.68rem',
  fontWeight: 900,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
};

const featuredTitleStyle: CSSProperties = {
  fontSize: 'var(--text-2xl)',
  fontWeight: 900,
  lineHeight: 1.08,
  marginTop: 'var(--space-1)',
};

const featuredSubStyle: CSSProperties = {
  color: 'var(--color-text-secondary)',
  fontSize: 'var(--text-sm)',
  marginTop: '2px',
};

const featuredArrowStyle: CSSProperties = {
  color: 'white',
  background: 'var(--color-blue)',
  width: 38,
  height: 38,
  minWidth: 38,
  borderRadius: 'var(--radius-md)',
  display: 'grid',
  placeItems: 'center',
  fontSize: '1.8rem',
  lineHeight: 1,
  boxShadow: '0 10px 20px rgba(0, 122, 255, 0.22)',
};

const offsetPreviewStyle: CSSProperties = {
  borderRadius: 'var(--radius-md)',
  background: 'var(--color-diagram-bg)',
  overflow: 'hidden',
  padding: 'var(--space-2)',
};

const continueLinkStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr) 24px',
  alignItems: 'center',
  gap: 'var(--space-2)',
  minHeight: 58,
  marginBottom: 'var(--space-3)',
  padding: 'var(--space-3) var(--space-4)',
  color: 'var(--color-text-primary)',
  background: 'var(--color-surface)',
  border: '1px solid var(--color-blue)',
  borderRadius: 'var(--radius-md)',
  boxShadow: '0 12px 28px rgba(0, 122, 255, 0.12)',
  textDecoration: 'none',
};

const continueLabelStyle: CSSProperties = {
  color: 'var(--color-blue)',
  fontSize: '0.65rem',
  fontWeight: 900,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
};

const continueTitleStyle: CSSProperties = {
  color: 'var(--color-text-primary)',
  fontSize: 'var(--text-base)',
  fontWeight: 900,
  lineHeight: 1.1,
  overflow: 'visible',
  textOverflow: 'clip',
  whiteSpace: 'normal',
};

const continueMetaStyle: CSSProperties = {
  color: 'var(--color-text-secondary)',
  fontSize: 'var(--text-xs)',
  fontWeight: 800,
  whiteSpace: 'nowrap',
};

const continueArrowStyle: CSSProperties = {
  width: 24,
  height: 24,
  borderRadius: 'var(--radius-pill)',
  color: 'white',
  background: 'var(--color-blue)',
  display: 'grid',
  placeItems: 'center',
  fontSize: '1.25rem',
  lineHeight: 1,
};

const levelToolCardStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '50px 1fr auto',
  alignItems: 'center',
  gap: 'var(--space-3)',
  minHeight: 70,
  padding: 'var(--space-3)',
  marginBottom: 'var(--space-3)',
  color: 'var(--color-text-primary)',
  background: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-md)',
  boxShadow: 'var(--shadow-card)',
  textDecoration: 'none',
};

const levelToolIconStyle: CSSProperties = {
  width: 50,
  height: 50,
  borderRadius: 'var(--radius-md)',
  background: 'var(--color-blue-soft)',
  display: 'grid',
  placeItems: 'center',
};

const levelToolTitleStyle: CSSProperties = {
  color: 'var(--color-text-primary)',
  fontSize: 'var(--text-lg)',
  fontWeight: 900,
  lineHeight: 1.1,
};

const levelToolSubStyle: CSSProperties = {
  color: 'var(--color-text-muted)',
  fontSize: 'var(--text-sm)',
  lineHeight: 1.2,
};

const levelToolBadgeStyle: CSSProperties = {
  color: 'white',
  background: 'var(--color-blue)',
  borderRadius: 'var(--radius-pill)',
  padding: 'var(--space-1) var(--space-3)',
  fontSize: 'var(--text-xs)',
  fontWeight: 900,
};

const featuredMetricGridStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
  gap: 'var(--space-2)',
};

const miniMetricStyle: CSSProperties = {
  display: 'grid',
  gap: '2px',
  padding: 'var(--space-2)',
  background: 'var(--color-bg)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-md)',
};

const miniMetricHighlightedStyle: CSSProperties = {
  ...miniMetricStyle,
  background: 'var(--color-blue)',
  borderColor: 'var(--color-blue)',
};

const miniMetricLabelStyle: CSSProperties = {
  color: 'var(--color-text-muted)',
  fontSize: '0.6rem',
  fontWeight: 850,
  letterSpacing: '0.07em',
  textTransform: 'uppercase',
};

const miniMetricLabelHighlightedStyle: CSSProperties = {
  ...miniMetricLabelStyle,
  color: 'rgba(255,255,255,0.78)',
};

const miniMetricValueStyle: CSSProperties = {
  color: 'var(--color-text-primary)',
  fontSize: 'var(--text-sm)',
  fontWeight: 900,
  lineHeight: 1,
  whiteSpace: 'nowrap',
};

const miniMetricValueHighlightedStyle: CSSProperties = {
  ...miniMetricValueStyle,
  color: 'white',
};

const bendCardStyle: CSSProperties = {
  background: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-md)',
  padding: 'var(--space-4)',
  display: 'grid',
  gridTemplateColumns: '58px 1fr 30px',
  alignItems: 'center',
  gap: 'var(--space-3)',
  minHeight: 88,
  marginBottom: 'var(--space-3)',
  boxShadow: 'var(--shadow-card)',
  textDecoration: 'none',
};

const compactBendCardStyle: CSSProperties = {
  ...bendCardStyle,
  minHeight: 78,
  padding: 'var(--space-3)',
  gridTemplateColumns: '50px 1fr 26px',
};

const tileBendCardStyle: CSSProperties = {
  ...bendCardStyle,
  gridTemplateColumns: '46px minmax(0, 1fr) 18px',
  gap: 'var(--space-3)',
  minHeight: 82,
  marginBottom: 0,
  padding: 'var(--space-3)',
  boxShadow: 'none',
};

function bendTitleRowStyle(locked: boolean): CSSProperties {
  if (locked) {
    return {
      display: 'grid',
      gridTemplateColumns: 'minmax(0, 1fr) auto',
      alignItems: 'baseline',
      columnGap: 'var(--space-2)',
      rowGap: 'var(--space-1)',
      minWidth: 0,
      width: '100%',
    };
  }

  return {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'nowrap',
    gap: 'var(--space-2)',
    minWidth: 0,
  };
}

function bendTitleTextStyle(locked: boolean, tile = false): CSSProperties {
  return {
    color: 'var(--color-text-primary)',
    fontSize: tile ? 'var(--text-base)' : 'var(--text-lg)',
    fontWeight: 850,
    lineHeight: tile ? 1.08 : 1.15,
    minWidth: 0,
    overflow: locked ? 'visible' : 'hidden',
    textOverflow: locked ? 'clip' : 'ellipsis',
    whiteSpace: locked ? 'normal' : 'nowrap',
  };
}

const lockedBadgeStyle: CSSProperties = {
  justifySelf: 'start',
  color: 'var(--color-accent)',
  background: 'rgba(249, 115, 22, 0.14)',
  border: '1px solid rgba(249, 115, 22, 0.22)',
  borderRadius: 'var(--radius-pill)',
  padding: '2px var(--space-2)',
  fontSize: '0.64rem',
  fontWeight: 900,
  textTransform: 'uppercase',
};

const bendResultCueStyle: CSSProperties = {
  justifySelf: 'start',
  marginTop: 'var(--space-1)',
  color: 'var(--color-blue)',
  background: 'var(--color-blue-soft)',
  border: '1px solid rgba(0, 122, 255, 0.16)',
  borderRadius: 'var(--radius-pill)',
  padding: '3px var(--space-2)',
  fontSize: '0.68rem',
  fontWeight: 900,
  lineHeight: 1,
  whiteSpace: 'normal',
};

const bendChevronStyle: CSSProperties = {
  justifySelf: 'end',
  color: 'var(--color-accent)',
  fontSize: 'var(--text-2xl)',
  lineHeight: 1,
};

const moreBendsSectionStyle: CSSProperties = {
  display: 'grid',
  gap: 'var(--space-3)',
};

const moreBendsHeaderStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr auto',
  alignItems: 'center',
  gap: 'var(--space-3)',
  padding: '0 var(--space-1)',
};

const moreBendsEyebrowStyle: CSSProperties = {
  color: 'var(--color-accent)',
  fontSize: '0.64rem',
  fontWeight: 900,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
};

const moreBendsTitleStyle: CSSProperties = {
  color: 'var(--color-text-primary)',
  fontSize: 'var(--text-xl)',
  fontWeight: 900,
  lineHeight: 1.05,
};

const moreBendsSubStyle: CSSProperties = {
  color: 'var(--color-text-muted)',
  fontSize: 'var(--text-sm)',
  lineHeight: 1.25,
  margin: 0,
};

const moreBendsBadgeStyle: CSSProperties = {
  justifySelf: 'end',
  color: 'var(--color-accent)',
  background: 'rgba(249, 115, 22, 0.14)',
  border: '1px solid rgba(249, 115, 22, 0.24)',
  borderRadius: 'var(--radius-pill)',
  padding: 'var(--space-1) var(--space-3)',
  fontSize: 'var(--text-xs)',
  fontWeight: 900,
  textTransform: 'uppercase',
};

const moreBendsGridStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 260px), 1fr))',
  gap: 'var(--space-3)',
  background: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-md)',
  padding: 'var(--space-3)',
  boxShadow: 'var(--shadow-card)',
};
